import { TFunction } from 'i18next';
import { WfReservedOptions } from '../../stores/WfFilterStore';
import { WfOperationStatusCode } from '../../models/enums/WfOperationtStatus';

interface wfWorkplaceCSVRequestSearchParams {
  reserved: string;
  branch: string;
  salesOffice?: string;
  section?: string;
  squad?: string;
  statusList?: WfOperationStatusCode[];
  customerNumber?: string;
  disasterId: string;
}

type CsvStatusKey = (typeof CSV_STATUS_KEYS)[number];

export interface wfWorkplaceCSVRequestBody {
  searchParams: wfWorkplaceCSVRequestSearchParams;
  translation: {
    disasterInformationName: string;
    searchTarget: string;
    csvHeaders: string[];
    statuses: Record<CsvStatusKey, string>;
    floodCategoryOptions: Record<string, string>;
    floodedAreaOptions: Record<string, string>;
    priorityOptions: Record<string, string>;
    propertyList: Record<string, string>;
    reservationOptions: Record<string, string>;
  };
  dateTimeFormat: string;
  timeZone: string;
}

const CSV_HEADER_KEYS = [
  'reservationStatus',
  'organizationCode',
  'salesOfficeName',
  'customerNumber',
  'buildingName',
  'productNumber',
  'unit',
  'incompleteAlerts',
  'onCall',
  'alert',
  'confinement',
  'powerOutage',
  'responseInput',
  'floodRequired',
  'floodCategory',
  'floodedLocation',
  'drainageRequired',
  'pitDepth',
  'waterDepth',
  'pitWidth',
  'pitLength',
  'priority',
  'estimatedWorkTime',
  'floodComments',
  'floodPhotoIds',
  'partsRequired',
  'partsNumber',
  'partsName',
  'quantity',
  'arrangement',
  'deliveryAddress',
  'estimatedArrivalTime',
  'requestNumber',
  'workOrderNumber',
  'partsComments',
  'partsPhotoIds',
  'workSubmissionStatus',
  'workRemarks',
  'status',
];

const CSV_STATUS_KEYS = [
  'entrapment',
  'oncall',
  'alert',
  'otherAlert',
  'partsReplace',
  'floodResponse',
  'powerOutage',
  'recoveryCompleted',
  'siteInaccessible',
  'unaddressed',
];

export const prepareWfWorkplaceCSVRequestBody = (
  t: TFunction<'translation', undefined>,
  params: {
    reserved?: WfReservedOptions;
    branch: string;
    salesOffice?: string;
    section?: string;
    squad?: string;
    statusList?: WfOperationStatusCode[];
    customerNumber?: string;
    disasterId?: string;
  }
): wfWorkplaceCSVRequestBody => {
  const formatValue = (val: string | number | undefined | null): string | null => {
    const s = String(val ?? '');
    return !s ? null : s;
  };

  const searchParams: wfWorkplaceCSVRequestSearchParams = {
    reserved: formatValue(params.reserved) ?? '',
    branch: formatValue(params.branch) ?? '',
    salesOffice: formatValue(params.salesOffice) ?? '',
    section: formatValue(params.section) ?? '',
    squad: formatValue(params.squad) ?? '',
    statusList: Array.isArray(params.statusList) ? params.statusList : [],
    customerNumber: formatValue(params.customerNumber) ?? '',
    disasterId: formatValue(params.disasterId) ?? '',
  };

  return {
    searchParams,
    translation: {
      disasterInformationName: t('wf.csv.disasterInformationName'),
      searchTarget: t('wf.csv.searchTarget'),
      csvHeaders: CSV_HEADER_KEYS.map((key) => t(`wf.csv.wfCsvHeaders.${key}`)),
      statuses: Object.fromEntries(CSV_STATUS_KEYS.map((key) => [key, t(`wf.statusCodes.${key}`)])),
      floodCategoryOptions: t('wf.csv.floodCategoryOptions', { returnObjects: true }) as Record<
        string,
        string
      >,
      floodedAreaOptions: t('wf.csv.floodedAreaOptions', { returnObjects: true }) as Record<
        string,
        string
      >,
      priorityOptions: t('wf.csv.priorityOptions', { returnObjects: true }) as Record<
        string,
        string
      >,
      propertyList: t('wf.csv.propertyList', { returnObjects: true }) as Record<string, string>,
      reservationOptions: t('wf.csv.reservationOptions', { returnObjects: true }) as Record<
        string,
        string
      >,
    },
    dateTimeFormat: 'yyyy/MM/dd HH:mm',
    timeZone: 'Europe/Zagreb',
  };
};
